import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Calendar, Clock, Phone, Mail, User, AlertCircle, RefreshCw, X } from 'lucide-react';
import { automationApi } from '../../../services/automationapi';
import { CustomSelect } from './CustomSelect';
import { CustomConfirm } from './CustomConfirm';
import toast from 'react-hot-toast';

interface OnCallMember {
  name: string;
  email: string;
  phone: string;
  telegram_chat_id?: string;
  _index?: number;
}

interface OnCallSchedule {
  id: number;
  name: string;
  client_id: number | null;
  rotation_type: 'daily' | 'weekly';
  start_date: string;
  handoff_time: string;
  timezone_offset_minutes: number;
  members: OnCallMember[];
  escalate_after_minutes: number;
  is_active: boolean;
  current_member?: OnCallMember;
}

export const OnCallTab: React.FC = () => {
  const [schedules, setSchedules] = useState<OnCallSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // Form State
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [name, setName] = useState('');
  const [rotationType, setRotationType] = useState<'daily' | 'weekly'>('weekly');
  const [startDate, setStartDate] = useState('');
  const [handoffTime, setHandoffTime] = useState('09:00');
  const [escalateMinutes, setEscalateMinutes] = useState(15);
  const [isActive, setIsActive] = useState(true);
  const [members, setMembers] = useState<OnCallMember[]>([]);

  // Member editing fields
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberPhone, setMemberPhone] = useState('');
  const [memberTelegram, setMemberTelegram] = useState('');

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const res = await automationApi.getOncall();
      if (res.data.status) {
        setSchedules(res.data.data);
      }
    } catch (err) {
      console.error(err);
      // Fallback mocks
      setSchedules([
        {
          id: 1,
          name: 'NOC rotation',
          client_id: null,
          rotation_type: 'weekly',
          start_date: '2026-07-20',
          handoff_time: '09:00',
          timezone_offset_minutes: 330,
          escalate_after_minutes: 15,
          is_active: true,
          members: [
            { name: 'Alice Smith', email: 'alice@company.com', phone: '+919876543210', telegram_chat_id: 'alice_noc' },
            { name: 'Bob Jones', email: 'bob@company.com', phone: '+918765432109', telegram_chat_id: 'bob_noc' }
          ],
          current_member: { name: 'Alice Smith', email: 'alice@company.com', phone: '+919876543210', telegram_chat_id: 'alice_noc', _index: 0 }
        },
        {
          id: 2,
          name: 'SecOps Level 2',
          client_id: 1,
          rotation_type: 'daily',
          start_date: '2026-07-22',
          handoff_time: '18:00',
          timezone_offset_minutes: 330,
          escalate_after_minutes: 30,
          is_active: true,
          members: [
            { name: 'John Doe', email: 'john@company.com', phone: '+917654321098' },
            { name: 'Clara Oswald', email: 'clara@company.com', phone: '+916543210987' }
          ],
          current_member: { name: 'John Doe', email: 'john@company.com', phone: '+917654321098', _index: 0 }
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const openCreateModal = () => {
    setEditingId(null);
    setName('');
    setRotationType('weekly');
    setStartDate(new Date().toISOString().split('T')[0]);
    setHandoffTime('09:00');
    setEscalateMinutes(15);
    setIsActive(true);
    setMembers([]);
    clearMemberForm();
    setShowModal(true);
  };

  const openEditModal = (schedule: OnCallSchedule) => {
    setEditingId(schedule.id);
    setName(schedule.name);
    setRotationType(schedule.rotation_type);
    setStartDate(schedule.start_date);
    setHandoffTime(schedule.handoff_time);
    setEscalateMinutes(schedule.escalate_after_minutes);
    setIsActive(schedule.is_active);
    setMembers([...schedule.members]);
    clearMemberForm();
    setShowModal(true);
  };

  const clearMemberForm = () => {
    setMemberName('');
    setMemberEmail('');
    setMemberPhone('');
    setMemberTelegram('');
  };

  const handleAddMember = () => {
    if (!memberName || !memberEmail || !memberPhone) {
      toast.error('Name, Email and Phone are required for shift members');
      return;
    }
    const newMember: OnCallMember = {
      name: memberName,
      email: memberEmail,
      phone: memberPhone,
      telegram_chat_id: memberTelegram || undefined
    };
    setMembers(prev => [...prev, newMember]);
    clearMemberForm();
  };

  const handleRemoveMember = (idx: number) => {
    setMembers(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) {
      toast.error('Schedule name is required');
      return;
    }
    if (members.length === 0) {
      toast.error('At least one team member must be added to the rotation');
      return;
    }

    const payload = {
      name,
      client_id: null,
      rotation_type: rotationType,
      start_date: startDate,
      handoff_time: handoffTime,
      timezone_offset_minutes: 330,
      members,
      escalate_after_minutes: escalateMinutes,
      is_active: isActive
    };

    try {
      if (editingId) {
        await automationApi.updateOncall(editingId, payload);
        toast.success('Schedule updated');
      } else {
        await automationApi.createOncall(payload);
        toast.success('Schedule created');
      }
      setShowModal(false);
      fetchSchedules();
    } catch (err) {
      // Mock save
      if (editingId) {
        setSchedules(prev => prev.map(s => s.id === editingId ? { ...s, ...payload, current_member: members[0] } : s));
        toast.success('Schedule updated (demo)');
      } else {
        setSchedules(prev => [...prev, { id: Date.now(), ...payload, current_member: members[0] }]);
        toast.success('Schedule created (demo)');
      }
      setShowModal(false);
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    try {
      await automationApi.deleteOncall(id);
      toast.success('Schedule deleted');
      setSchedules(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      setSchedules(prev => prev.filter(s => s.id !== id));
      toast.success('Schedule deleted (demo)');
    }
  };

  return (
    <div className="flex flex-col h-full space-y-2.5">
      {/* Action Header */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 bg-gray-800/40 p-2.5 border border-gray-700/50 rounded-xl backdrop-blur-md">
        <div>
          <h2 className="text-xs font-bold text-white uppercase tracking-wider">On-Call Rotations & Escalation</h2>
          <p className="text-[11px] text-gray-200">Manage support shifts, computed current active members, and secondary paging details.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={fetchSchedules}
            className="p-1.5 border border-gray-700 hover:border-gray-600 rounded-lg bg-gray-900 text-gray-200 hover:text-white transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white text-[11px] font-bold rounded-lg transition-all duration-150 shadow-md hover:shadow-orange-500/10"
          >
            <Plus className="w-3.5 h-3.5" />
            ADD ROTATION
          </button>
        </div>
      </div>

      {/* Roster Listing */}
      <div className="flex-1 overflow-y-auto custom-scroll space-y-2.5">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
          </div>
        ) : schedules.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-200 space-y-2 border border-gray-700/50 rounded-xl bg-gray-800/20 backdrop-blur-md">
            <AlertCircle className="w-8 h-8 text-gray-200" />
            <p className="text-xs">No on-call schedules configured</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className="bg-gray-800/40 p-3.5 rounded-xl border border-gray-700/50 backdrop-blur-md flex flex-col justify-between space-y-3 group hover:border-orange-500/30 transition-all duration-200"
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xs font-bold text-white group-hover:text-orange-400 transition-colors">
                        {schedule.name}
                      </h3>
                      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${schedule.is_active ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-gray-800 text-gray-200'
                        }`}>
                        {schedule.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[9px] text-gray-200">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {schedule.rotation_type}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Handoff: {schedule.handoff_time}</span>
                      <span>Escalate: {schedule.escalate_after_minutes} mins</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditModal(schedule)}
                      className="p-1 hover:bg-gray-700/50 rounded-md text-gray-200 hover:text-white transition-colors"
                      title="Edit schedule"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(schedule.id)}
                      className="p-1 hover:bg-red-950/20 rounded-md text-gray-200 hover:text-red-400 transition-colors cursor-pointer"
                      title="Delete schedule"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Now On-call Card */}
                {schedule.is_active && schedule.current_member ? (
                  <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-lg p-2.5 space-y-1.5 relative overflow-hidden">
                    <div className="absolute right-1.5 top-1.5 text-[8px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded-full tracking-wider uppercase">
                      NOW ON CALL
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white">
                        <User className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-white leading-none">{schedule.current_member.name}</div>
                        <div className="text-[9px] text-gray-200 mt-0.5">Shift Index: #{schedule.current_member._index ?? 0}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-200 pt-1 border-t border-orange-500/10">
                      <div className="flex items-center gap-1 text-[11px] truncate">
                        <Mail className="w-3 h-3 text-orange-400 flex-shrink-0" />
                        <span className="truncate">{schedule.current_member.email}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[11px] truncate">
                        <Phone className="w-3 h-3 text-orange-400 flex-shrink-0" />
                        <span>{schedule.current_member.phone}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-900 border border-gray-800/80 rounded-lg p-3 text-center text-[11px] text-gray-200 italic">
                    Schedule is inactive or has no active shift
                  </div>
                )}

                {/* Member Roster list */}
                <div className="space-y-1">
                  <h4 className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Rotational Order</h4>
                  <div className="flex flex-wrap gap-1.5 max-h-16 overflow-y-auto custom-scroll">
                    {schedule.members.map((m, i) => (
                      <span
                        key={i}
                        className={`text-[10px] px-2 py-0.5 rounded border ${schedule.current_member?.name === m.name
                            ? 'bg-orange-500/10 border-orange-500/30 text-orange-400 font-bold'
                            : 'bg-gray-900 border-gray-800 text-gray-200'
                          }`}
                      >
                        {i + 1}. {m.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CRUD Form Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700/80 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700/60 bg-gray-950/40">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                {editingId ? 'Edit On-Call Schedule' : 'Create On-Call Schedule'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 text-gray-200 hover:text-white rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSaveSchedule} className="flex-1 overflow-y-auto custom-scroll p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1">
                  <label className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Schedule Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="e.g. Server Ops On-Call"
                    className="w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Rotation Period</label>
                  <CustomSelect
                    value={rotationType}
                    onChange={(val) => setRotationType(val as 'daily' | 'weekly')}
                    className="w-full"
                    options={[
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                    ]}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Escalate Delay (Minutes)</label>
                  <input
                    type="number"
                    value={escalateMinutes}
                    onChange={e => setEscalateMinutes(parseInt(e.target.value))}
                    min={0}
                    className="w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Rotation Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Handoff Time (HH:MM)</label>
                  <input
                    type="time"
                    value={handoffTime}
                    onChange={e => setHandoffTime(e.target.value)}
                    className="w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500"
                    required
                  />
                </div>

                <div className="col-span-2 flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                    className="accent-orange-500 rounded"
                  />
                  <label htmlFor="isActive" className="text-xs text-gray-200 font-medium">Activate Rotation immediately</label>
                </div>
              </div>

              {/* Roster Builder */}
              <div className="border-t border-gray-800 pt-4 space-y-3">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider">Configure Rotation Roster</h4>

                {/* Roster order list */}
                {members.length > 0 ? (
                  <div className="space-y-2 bg-gray-950/50 p-3 rounded-lg border border-gray-800/80">
                    <div className="text-[10px] font-bold text-gray-200 uppercase tracking-wider mb-1">Current Roster Order</div>
                    <div className="divide-y divide-gray-800">
                      {members.map((member, index) => (
                        <div key={index} className="flex items-center justify-between py-1.5 text-xs text-white">
                          <span className="font-semibold text-orange-400 w-6">#{index + 1}</span>
                          <span className="flex-1">{member.name} ({member.email})</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(index)}
                            className="p-1 hover:bg-red-950/20 text-gray-200 hover:text-red-400 rounded transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-200 italic p-3 text-center border border-dashed border-gray-800 rounded-lg">
                    Roster is empty. Add team members below.
                  </div>
                )}

                {/* Add member form block */}
                <div className="bg-gray-950/30 p-3 rounded-lg border border-gray-800/40 grid grid-cols-2 gap-3">
                  <div className="text-xs font-bold text-white col-span-2">Add Member to Rotation</div>
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={memberName}
                    onChange={e => setMemberName(e.target.value)}
                    className="bg-gray-950 border border-gray-800 rounded px-2.5 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={memberEmail}
                    onChange={e => setMemberEmail(e.target.value)}
                    className="bg-gray-950 border border-gray-800 rounded px-2.5 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  />
                  <input
                    type="text"
                    placeholder="Phone Number (e.g. +1...)"
                    value={memberPhone}
                    onChange={e => setMemberPhone(e.target.value)}
                    className="bg-gray-950 border border-gray-800 rounded px-2.5 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  />
                  <input
                    type="text"
                    placeholder="Telegram Chat ID (Optional)"
                    value={memberTelegram}
                    onChange={e => setMemberTelegram(e.target.value)}
                    className="bg-gray-950 border border-gray-800 rounded px-2.5 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddMember}
                    className="col-span-2 py-1.5 bg-gray-800 hover:bg-gray-800 text-xs font-bold rounded text-white border border-gray-700"
                  >
                    ADD MEMBER TO LIST
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-gray-800 pt-4 flex items-center justify-end gap-2 bg-gray-950/20 -mx-4 -mb-4 p-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-700 text-gray-200 hover:text-white rounded-lg text-xs font-bold hover:bg-gray-800"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white rounded-lg text-xs font-bold transition-all shadow-md hover:shadow-orange-500/10"
                >
                  SAVE SCHEDULE
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CustomConfirm
        isOpen={deleteConfirmId !== null}
        title="Delete On-Call Schedule"
        message="Are you sure you want to delete this support escalation schedule? This action cannot be undone."
        onConfirm={() => {
          if (deleteConfirmId !== null) {
            handleDeleteSchedule(deleteConfirmId);
            setDeleteConfirmId(null);
          }
        }}
        onCancel={() => setDeleteConfirmId(null)}
      />
    </div>
  );
};
